import React from 'react';
import { Helmet } from 'react-helmet';

/**
 * 增强版SEO头部组件
 * 支持标准元数据、Open Graph标签、Twitter Cards和结构化数据
 * 
 * @param {string} title - 页面标题
 * @param {string} description - 页面描述
 * @param {string} canonicalUrl - 规范URL
 * @param {object} schemaData - 结构化数据对象
 * @param {string} imageUrl - 分享图片URL
 * @param {Array} keywords - 关键词数组
 * @param {string} ogType - Open Graph类型，默认为website
 * @param {string} locale - 语言区域设置，默认为zh_CN
 * @param {string} twitterSite - Twitter站点账号
 * @param {string} twitterCreator - Twitter创建者账号
 * @param {string} themeColor - 主题颜色
 * @param {object} additionalMetaTags - 额外的元标签
 */
const SeoHead = ({ 
  title, 
  description, 
  canonicalUrl, 
  schemaData = null,
  imageUrl = null,
  keywords = [],
  ogType = 'website',
  locale = 'zh_CN',
  twitterSite = '@DestinyCrystal',
  twitterCreator = '@DestinyCrystal',
  themeColor = '#9c27b0',
  additionalMetaTags = {}
}) => {
  // 组装Open Graph类型标签
  const getOgType = () => {
    // 如果schemaData存在，尝试从中推断正确的og:type
    if (schemaData) {
      const schemaType = schemaData['@type'];
      if (schemaType === 'BlogPosting' || schemaType === 'Article') {
        return 'article';
      } else if (schemaType === 'Product') {
        return 'product';
      } else if (schemaType === 'Person') {
        return 'profile';
      }
    }
    return ogType; // 默认返回传入的ogType
  };
  
  // 标准化图片URL
  const normalizedImageUrl = imageUrl || 'https://destiny-crystal.com/assets/images/logo.png';
  
  // 生成基本站点名称
  const siteName = 'DestinyCrystal命理水晶';
  
  return (
    <Helmet>
      {/* 基本元数据标签 */}
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      <meta name="theme-color" content={themeColor} />
      
      {/* Open Graph标签 - 用于Facebook等社交媒体 */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:type" content={getOgType()} />
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:image" content={normalizedImageUrl} />
      <meta property="og:image:alt" content={title || siteName} />
      <meta property="og:locale" content={locale} />
      
      {/* 如果是文章类型，添加额外的文章标签 */}
      {getOgType() === 'article' && schemaData && (
        <>
          {schemaData.datePublished && 
            <meta property="article:published_time" content={schemaData.datePublished} />}
          {schemaData.dateModified && 
            <meta property="article:modified_time" content={schemaData.dateModified} />}
          {schemaData.author && schemaData.author.name && 
            <meta property="article:author" content={schemaData.author.name} />}
          {schemaData.publisher && schemaData.publisher.name && 
            <meta property="article:publisher" content={schemaData.publisher.name} />}
          {schemaData.keywords && 
            <meta property="article:tag" content={schemaData.keywords} />}
        </>
      )}
      
      {/* Twitter Card标签 */}
      <meta name="twitter:card" content="summary_large_image" />
      {twitterSite && <meta name="twitter:site" content={twitterSite} />}
      {twitterCreator && <meta name="twitter:creator" content={twitterCreator} />}
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={normalizedImageUrl} />
      
      {/* 添加额外的元标签 */}
      {Object.entries(additionalMetaTags).map(([name, content]) => (
        <meta key={name} name={name} content={content} />
      ))}
      
      {/* Schema.org结构化数据 */}
      {schemaData && (
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      )}
    </Helmet>
  );
};

export default SeoHead;